import { useMemo } from "react";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { resources } from "@constants/hardcoded-strings";
import { useTableFilters } from "@hooks/use-table-filters";
import styles from "./update-organization-users-table-container.less";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { UpdateOrganizationUsersTableCredentialCell } from "@containers/modals/update-organization-users-modal/update-organization-users-table-container/update-organization-users-table-credential-cell/update-organization-users-table-credential-cell";

type UpdateOrganizationUsersTableContainerProps = {
    organizationUsers: Array<AxnAdminUser>;
    initialOrganizationUsers: Array<AxnAdminUser>;
    handleOrganizationUserChange: (organizationUser: AxnAdminUser) => void;
};

export type UpdateOrganizationUsersTableData = {
    id: string;
    name: string;
    email: string;
    credential: string;
    initialCredential: string;
};

export const UpdateOrganizationUsersTableContainer = (props: UpdateOrganizationUsersTableContainerProps) => {
    const {
        organizationUsers,
        initialOrganizationUsers,
        handleOrganizationUserChange
    } = props;

    const organizationUsersTableData = useMemo(() => {
        return organizationUsers.map(ou => {
            const initialCredential = initialOrganizationUsers.find(iou => iou.id === ou.id)?.axnAdminUserDetails?.credential;
            let name = "";
            if (ou.axnAdminUserDetails.firstName) {
                name += ou.axnAdminUserDetails.firstName;
            }
            if (ou.axnAdminUserDetails.lastName) {
                if (ou.axnAdminUserDetails.firstName) {
                    name += " ";
                }
                name += ou.axnAdminUserDetails.lastName;
            }
            return {
                id: ou.id,
                name: name,
                email: ou.axnAdminUserDetails.email,
                credential: ou.axnAdminUserDetails.credential,
                initialCredential: initialCredential
            };
        }).filter(ou => ou.initialCredential);
    }, [organizationUsers]);

    const handleUserCredentialChange = (id: string, credential: string) => {
        const orgUser = organizationUsers.find(ou => ou.id === id);
        if (orgUser) {
            handleOrganizationUserChange({
                ...orgUser,
                axnAdminUserDetails: {
                    ...orgUser.axnAdminUserDetails,
                    credential: credential
                }
            });
        }
    };

    const columnHelper = createColumnHelper<UpdateOrganizationUsersTableData>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.updateOrganizationUsers.columns.name.header,
        }),
        columnHelper.accessor("email", {
            header: resources.tables.updateOrganizationUsers.columns.email.header,
        }),
        columnHelper.accessor("credential", {
            header: resources.tables.updateOrganizationUsers.columns.credential.header,
            cell: (props) => (
                <UpdateOrganizationUsersTableCredentialCell
                    data={props.row.original}
                    handleUserCredentialChange={handleUserCredentialChange}
                />
            )
        }),
    ]), [organizationUsersTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: organizationUsersTableData,
        filterFns: filterTypes,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="update-organization-users"
                useTableResult={useTableResult}
                className={styles.updateOrganizationUsersTable}
                headerColumnKeyPrefix={"update-organization-users"}
            />
        </IdwTableContainer>
    );
};